// Winner photos: where they're kept, the consent rule, and shrinking a phone
// photo in the admin's browser before it's uploaded.

export const WINNER_PHOTO_BUCKET = 'winner-photos';

/**
 * The winner's photo, but only if their consent is on record
 * (past_winners.winner_image_consent_at, set when an admin ticks the box).
 * The public pages read past_winners with the service role, so they apply
 * the rule here; get_public_winners() applies the same rule in the database.
 */
export function consentedPhoto(row) {
  const url = String(row?.winner_image_url || '').trim();
  return url && row?.winner_image_consent_at ? url : null;
}

/**
 * The photo's path inside the winner-photos bucket, from its public URL.
 * Null for anything stored elsewhere: older winner photos are in
 * competition-images, which the competition screens share, so the admin
 * screen never deletes from there.
 */
export function winnerPhotoPath(url) {
  const marker = `/storage/v1/object/public/${WINNER_PHOTO_BUCKET}/`;
  const s = String(url || '');
  const i = s.indexOf(marker);
  if (i === -1) return null;
  return decodeURIComponent(s.slice(i + marker.length).split('?')[0]) || null;
}

// Hosts next.config.mjs lets next/image resize (images.remotePatterns). Any
// other host is shown as it is, because next/image refuses unknown hosts.
const RESIZABLE_HOSTS = [
  'pvzgsnkmcrxfhojzbtcr.supabase.co',
  'images.unsplash.com',
  'images.pexels.com',
  'images.pixabay.com',
];

export function canResize(url) {
  try {
    const u = new URL(url);
    return u.protocol === 'https:' && RESIZABLE_HOSTS.includes(u.hostname);
  } catch {
    return false;
  }
}

/**
 * Shrinks a photo so its longer side is at most `maxSide` pixels and saves it
 * as a JPEG. Phone photos arrive at 3 to 10 MB; this brings them to a few
 * hundred KB. Drawing it afresh also drops the camera's hidden details,
 * including the GPS position where the photo was taken. Browser only.
 */
export async function shrinkPhoto(file, { maxSide = 1600, quality = 0.82 } = {}) {
  const src = URL.createObjectURL(file);
  try {
    const img = await new Promise((resolve, reject) => {
      const el = new window.Image();
      el.onload = () => resolve(el);
      el.onerror = () =>
        reject(new Error('That file could not be opened as a photo. Please use a JPG or PNG.'));
      el.src = src;
    });
    // Browsers turn the photo the right way up (from the camera's
    // orientation flag) both here and when drawing it.
    const scale = Math.min(1, maxSide / Math.max(img.naturalWidth, img.naturalHeight));
    const width = Math.max(1, Math.round(img.naturalWidth * scale));
    const height = Math.max(1, Math.round(img.naturalHeight * scale));
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    // JPEG has no transparency: a see-through PNG gets white, not black.
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, width, height);
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(img, 0, 0, width, height);
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', quality));
    if (!blob) throw new Error('The photo could not be compressed. Please try another one.');
    return { blob, width, height };
  } finally {
    URL.revokeObjectURL(src);
  }
}
